// Editor for the three sections of a knowledge_object payload:
//   1. Forbidden edges  — "X cannot cause Y"
//   2. Required edges   — "X is known to cause Y"
//   3. Tiers            — ordered groups of variables; later tiers cannot
//                         cause earlier tiers (Pearl-style temporal priors).
//
// Uncontrolled at the section level — the parent owns the payload (one of
// the four custom components in [[ui-template-based]]). All UI primitives
// come from shadcn/ui; no custom CSS beyond Tailwind composition.

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { KnowledgePayload } from '@/lib/knowledge';
import { ArrowDown, ArrowUp, Plus, X } from 'lucide-react';
import * as React from 'react';

interface KnowledgeEditorProps {
  // The dataset's column names — used to populate the source/target dropdowns
  // and the tier-add picker. Empty array is valid (renders disabled controls
  // with a "pick a dataset first" hint).
  columns: string[];
  value: KnowledgePayload;
  onChange: (next: KnowledgePayload) => void;
  disabled?: boolean;
  // Restrict rendering to a single section so callers can place each one in
  // its own Tab (DiscoveryNew inline editor). Default 'all' renders the
  // three stacked Cards as on the standalone /knowledge/new page.
  section?: 'all' | 'forbidden' | 'required' | 'tiers';
}

export function KnowledgeEditor({
  columns,
  value,
  onChange,
  disabled = false,
  section = 'all',
}: KnowledgeEditorProps) {
  const hasColumns = columns.length > 0;

  function setForbidden(rows: [string, string][]) {
    onChange({ ...value, edges_forbidden: rows });
  }
  function setRequired(rows: [string, string][]) {
    onChange({ ...value, edges_required: rows });
  }
  function setTiers(tiers: string[][]) {
    onChange({ ...value, tiers });
  }

  return (
    <div className="space-y-6">
      {section === 'all' || section === 'forbidden' ? (
        <EdgeSection
          title="Forbidden edges"
          description="Edges the algorithm should NEVER include (e.g. 'age cannot cause hair color')."
          addLabel="Add forbidden edge"
          emptyLabel="No forbidden edges yet."
          columns={columns}
          rows={value.edges_forbidden}
          onChange={setForbidden}
          disabled={disabled || !hasColumns}
        />
      ) : null}
      {section === 'all' || section === 'required' ? (
        <EdgeSection
          title="Required edges"
          description="Edges you know exist (e.g. 'smoking causes lung cancer')."
          addLabel="Add required edge"
          emptyLabel="No required edges yet."
          columns={columns}
          rows={value.edges_required}
          onChange={setRequired}
          disabled={disabled || !hasColumns}
        />
      ) : null}
      {section === 'all' || section === 'tiers' ? (
        <TiersSection
          columns={columns}
          tiers={value.tiers}
          onChange={setTiers}
          disabled={disabled || !hasColumns}
        />
      ) : null}
      {!hasColumns ? (
        <p className="text-xs text-muted-foreground">
          Pick a dataset above to populate variable choices.
        </p>
      ) : null}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Edge section (used for both forbidden + required — same UI, different copy).
// -----------------------------------------------------------------------------

interface EdgeSectionProps {
  title: string;
  description: string;
  addLabel: string;
  emptyLabel: string;
  columns: string[];
  rows: [string, string][];
  onChange: (rows: [string, string][]) => void;
  disabled: boolean;
}

function EdgeSection({
  title,
  description,
  addLabel,
  emptyLabel,
  columns,
  rows,
  onChange,
  disabled,
}: EdgeSectionProps) {
  function addRow() {
    onChange([...rows, ['', '']]);
  }
  function setEndpoint(index: number, side: 0 | 1, name: string) {
    const next: [string, string][] = rows.map((r, i) => {
      if (i !== index) return r;
      const copy: [string, string] = [...r];
      copy[side] = name;
      return copy;
    });
    onChange(next);
  }
  function removeRow(index: number) {
    onChange(rows.filter((_, i) => i !== index));
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">{emptyLabel}</p>
        ) : (
          <div className="space-y-2">
            {rows.map((row, idx) => (
              <EdgeRow
                // biome-ignore lint/suspicious/noArrayIndexKey: rows are positional + ephemeral
                key={idx}
                row={row}
                columns={columns}
                onSourceChange={(v) => setEndpoint(idx, 0, v)}
                onTargetChange={(v) => setEndpoint(idx, 1, v)}
                onRemove={() => removeRow(idx)}
                disabled={disabled}
              />
            ))}
          </div>
        )}
        <Button type="button" variant="outline" size="sm" onClick={addRow} disabled={disabled}>
          <Plus className="mr-1 h-4 w-4" /> {addLabel}
        </Button>
      </CardContent>
    </Card>
  );
}

interface EdgeRowProps {
  row: [string, string];
  columns: string[];
  onSourceChange: (value: string) => void;
  onTargetChange: (value: string) => void;
  onRemove: () => void;
  disabled: boolean;
}

function EdgeRow({
  row,
  columns,
  onSourceChange,
  onTargetChange,
  onRemove,
  disabled,
}: EdgeRowProps) {
  const [source, target] = row;
  const isSelfLoop = source && target && source === target;
  return (
    <div className="flex flex-wrap items-center gap-2">
      <ColumnSelect
        value={source}
        placeholder="Source"
        columns={columns}
        onChange={onSourceChange}
        disabled={disabled}
      />
      <span aria-hidden className="text-muted-foreground">
        →
      </span>
      <ColumnSelect
        value={target}
        placeholder="Target"
        columns={columns}
        onChange={onTargetChange}
        disabled={disabled}
      />
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={onRemove}
        disabled={disabled}
        aria-label="Remove edge"
      >
        <X className="h-4 w-4" />
      </Button>
      {isSelfLoop ? (
        <p className="basis-full text-xs text-destructive">
          Self-loops are not allowed — source and target must differ.
        </p>
      ) : null}
    </div>
  );
}

interface ColumnSelectProps {
  value: string;
  placeholder: string;
  columns: string[];
  onChange: (value: string) => void;
  disabled: boolean;
}

function ColumnSelect({ value, placeholder, columns, onChange, disabled }: ColumnSelectProps) {
  return (
    <Select value={value || undefined} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger className="w-44">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {columns.map((c) => (
          <SelectItem key={c} value={c}>
            {c}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// -----------------------------------------------------------------------------
// Tiers section.
// -----------------------------------------------------------------------------

interface TiersSectionProps {
  columns: string[];
  tiers: string[][];
  onChange: (tiers: string[][]) => void;
  disabled: boolean;
}

function TiersSection({ columns, tiers, onChange, disabled }: TiersSectionProps) {
  function addTier() {
    onChange([...tiers, []]);
  }
  function removeTier(index: number) {
    onChange(tiers.filter((_, i) => i !== index));
  }
  function moveTier(index: number, direction: -1 | 1) {
    const target = index + direction;
    if (target < 0 || target >= tiers.length) return;
    const next = tiers.slice();
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  }
  function addVar(tierIndex: number, name: string) {
    if (!name) return;
    const next = tiers.map((t, i) => {
      if (i !== tierIndex) return t;
      if (t.includes(name)) return t;
      return [...t, name];
    });
    onChange(next);
  }
  function removeVar(tierIndex: number, name: string) {
    onChange(tiers.map((t, i) => (i === tierIndex ? t.filter((n) => n !== name) : t)));
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Tiers</CardTitle>
        <CardDescription>
          Variables in earlier tiers can cause variables in later tiers, never the reverse. Use for
          temporal data (e.g. tier 1 = 'baseline metrics', tier 2 = 'outcomes').
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {tiers.length === 0 ? (
          <p className="text-sm text-muted-foreground">No tiers yet.</p>
        ) : (
          <div className="space-y-2">
            {tiers.map((tier, idx) => (
              <TierRow
                // biome-ignore lint/suspicious/noArrayIndexKey: positional
                key={idx}
                tierIndex={idx}
                tierCount={tiers.length}
                variables={tier}
                availableColumns={columns.filter((c) => !tier.includes(c))}
                onAddVar={(name) => addVar(idx, name)}
                onRemoveVar={(name) => removeVar(idx, name)}
                onMoveUp={() => moveTier(idx, -1)}
                onMoveDown={() => moveTier(idx, 1)}
                onRemove={() => removeTier(idx)}
                disabled={disabled}
              />
            ))}
          </div>
        )}
        <Button type="button" variant="outline" size="sm" onClick={addTier} disabled={disabled}>
          <Plus className="mr-1 h-4 w-4" /> Add tier
        </Button>
      </CardContent>
    </Card>
  );
}

interface TierRowProps {
  tierIndex: number;
  tierCount: number;
  variables: string[];
  availableColumns: string[];
  onAddVar: (name: string) => void;
  onRemoveVar: (name: string) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRemove: () => void;
  disabled: boolean;
}

function TierRow({
  tierIndex,
  tierCount,
  variables,
  availableColumns,
  onAddVar,
  onRemoveVar,
  onMoveUp,
  onMoveDown,
  onRemove,
  disabled,
}: TierRowProps) {
  // Local key bumps after each "add" so the Select resets back to placeholder
  // without us having to control its value externally.
  const [pickerKey, setPickerKey] = React.useState(0);

  function handleAdd(name: string) {
    onAddVar(name);
    setPickerKey((k) => k + 1);
  }

  return (
    <div className="rounded-md border p-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Badge variant="secondary">Tier {tierIndex + 1}</Badge>
        </div>
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={onMoveUp}
            disabled={disabled || tierIndex === 0}
            aria-label={`Move tier ${tierIndex + 1} up`}
          >
            <ArrowUp className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={onMoveDown}
            disabled={disabled || tierIndex === tierCount - 1}
            aria-label={`Move tier ${tierIndex + 1} down`}
          >
            <ArrowDown className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={onRemove}
            disabled={disabled}
            aria-label={`Remove tier ${tierIndex + 1}`}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {variables.length === 0 ? (
          <span className="text-xs text-muted-foreground">No variables in this tier.</span>
        ) : (
          variables.map((name) => (
            <Badge key={name} variant="outline" className="gap-1">
              {name}
              <button
                type="button"
                onClick={() => onRemoveVar(name)}
                disabled={disabled}
                className="-mr-1 ml-0.5 rounded hover:bg-muted"
                aria-label={`Remove ${name} from tier ${tierIndex + 1}`}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))
        )}
      </div>
      <div className="mt-2">
        <Select
          key={pickerKey}
          onValueChange={handleAdd}
          disabled={disabled || availableColumns.length === 0}
        >
          <SelectTrigger className="w-48">
            <SelectValue
              placeholder={
                availableColumns.length === 0 ? 'All variables added' : 'Add variable to tier'
              }
            />
          </SelectTrigger>
          <SelectContent>
            {availableColumns.map((c) => (
              <SelectItem key={c} value={c}>
                {c}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
